/** Shapes shared by the staff calling campaigns (member win-back, candidate outreach). */

export type Tone = "emerald" | "amber" | "steel" | "red" | "slate" | "violet";

export interface OutcomeDef<K extends string = string> {
  key: K;
  label: string;
  short: string;
  tone: Tone;
  /** Heading the outcome is listed under in a grouped picker. */
  group?: string;
  /** No longer offered, but still shown on calls already logged with it. */
  retired?: boolean;
  /** Can't be saved without a note saying what happened. */
  needsNote?: boolean;
}

/** One logged attempt, as both campaigns return it. */
export interface OutreachEntryBase<K extends string = string> {
  id: string;
  outcome: K;
  /** Every outcome of the call (campaigns with multi-select outcomes); `outcome` is the first. */
  outcomes?: K[];
  note: string | null;
  created_at: string;
  updated_at: string;
  edited: boolean;
  actor: { id: string; name: string | null } | null;
  can_edit: boolean;
}

export interface StaffOption {
  id: string;
  name: string;
  count: number;
}

export interface RegionCounts {
  region: string;
  matching: number;
  [bucket: string]: number | string;
}

/** Fetches and saves a CSV the server built; the filename comes from the response. */
export async function saveCsvResponse(res: Response, fallback: string): Promise<void> {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail || `Export failed (${res.status})`), { status: res.status });
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") || "")?.[1] || fallback;
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Appends a list filter as repeated params. */
export function appendList(p: URLSearchParams, key: string, values: readonly (string | number)[] | undefined | null) {
  for (const v of values ?? []) p.append(key, String(v));
}
